import { useState, useEffect, useCallback, useRef } from "react";
import DiscussionCard from "../components/DiscussionCard";
import { getDiscussions, createDiscussion } from "../api";
import { useAuth } from "../context/AuthContext";

const TYPES = [
  { key: "all",     label: "all" },
  { key: "battle",  label: "⚔ battles" },
  { key: "hottake", label: "🔥 hot takes" },
  { key: "scene",   label: "🎬 scene reactions" },
  { key: "predict", label: "🎯 predictions" },
  { key: "spoiler", label: "💀 spoiler room" },
  { key: "general", label: "💬 general" },
];

const SORT_OPTIONS = ["latest", "trending", "most debated"];

const POST_TYPES = [
  { key: "battle",  icon: "⚔",  name: "guff battle",    desc: "two sides, community votes", bg: "#2a1510" },
  { key: "hottake", icon: "🔥", name: "hot take",       desc: "agree or disagree",          bg: "#1a1025" },
  { key: "scene",   icon: "🎬", name: "scene reaction", desc: "tag a moment, react",        bg: "#101a10" },
  { key: "predict", icon: "🎯", name: "prediction",     desc: "guess before watching",      bg: "#1a1500" },
  { key: "spoiler", icon: "💀", name: "spoiler room",   desc: "full discussion, gated",     bg: "#1a1010" },
  { key: "general", icon: "💬", name: "general",         desc: "just start a guff",          bg: "#101520" },
];

function totalVotes(d) {
  return (d.votes?.a || 0) + (d.votes?.b || 0) +
         (d.votes?.agree || 0) + (d.votes?.disagree || 0);
}

export default function Discussions() {
  const { currentUser, token, showAuthGate } = useAuth();

  const [activeType,   setActiveType]   = useState("all");
  const [activeSort,   setActiveSort]   = useState("latest");
  const [discussions,  setDiscussions]  = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [showForm,     setShowForm]     = useState(false);
  const [formType,     setFormType]     = useState(null);
  const [form,         setForm]         = useState({ question: "", side_a: "", side_b: "", quote: "", scene_tag: "" });
  const [submitting,   setSubmitting]   = useState(false);
  const [formError,    setFormError]    = useState("");
  const [toast,        setToast]        = useState("");

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  }

  const fetchDiscussions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { per_page: 50, general: "true" };
      if (activeType !== "all") params.type = activeType;
      const data  = await getDiscussions(params, token);
      let items   = data.discussions || [];

      if (activeSort === "trending") {
        items = [...items].sort((a, b) => totalVotes(b) - totalVotes(a));
      } else if (activeSort === "most debated") {
        items = [...items].sort((a, b) => (b.reply_count || 0) - (a.reply_count || 0));
      }
      setDiscussions(items);
    } catch {
      setDiscussions([]);
    } finally {
      setLoading(false);
    }
  }, [activeType, activeSort]);

  useEffect(() => { fetchDiscussions(); }, [fetchDiscussions]);

  // Sidebar: derived from fetched discussions
  const weeklyBattle = [...discussions]
    .filter(d => d.type === "battle")
    .sort((a, b) => ((b.votes?.a || 0) + (b.votes?.b || 0)) - ((a.votes?.a || 0) + (a.votes?.b || 0)))[0] || null;

  const trendingDebates = [...discussions]
    .sort((a, b) => totalVotes(b) - totalVotes(a))
    .slice(0, 5);

  const formRef = useRef(null);

  function openForm(type = null) {
    if (!token) { showAuthGate("start a guff"); return; }
    setShowForm(true);
    setFormType(type);
    setFormError("");
    setForm({ question: "", side_a: "", side_b: "", quote: "", scene_tag: "" });
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  }

  async function handleSubmit() {
    if (!form.question.trim()) { setFormError("What's the question?"); return; }
    if (!token) { showAuthGate("start a guff"); return; }
    setSubmitting(true);
    setFormError("");
    try {
      const disc = await createDiscussion({
        type:     formType || "general",
        question: form.question.trim(),
        ...(formType === "battle" && { side_a: form.side_a.trim(), side_b: form.side_b.trim() }),
        ...(formType === "scene"  && { quote: form.quote.trim(), scene_tag: form.scene_tag.trim() }),
      }, token);
      if (disc.id) {
        setDiscussions(prev => [disc, ...prev]);
        setShowForm(false);
        setFormType(null);
      } else {
        setFormError(disc.error || "Failed to post — check you're signed in");
      }
    } catch {
      setFormError("Failed to post — try again");
    } finally {
      setSubmitting(false);
    }
  }

  function handleDelete(disc_id) {
    setDiscussions(prev => prev.filter(d => d.id !== disc_id));
  }

  function handleUpdate(updated) {
    setDiscussions(prev => prev.map(d => d.id === updated.id ? updated : d));
  }

  const iStyle = {
    width: "100%", background: "#161618", border: "0.5px solid #252528",
    borderRadius: 8, padding: "10px 12px", fontSize: 12, color: "#c0c0c0",
    fontFamily: "'Plus Jakarta Sans', sans-serif", outline: "none",
    boxSizing: "border-box",
  };

  const activeFormType = POST_TYPES.find(p => p.key === formType);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  return (
    <div style={{ minHeight: "100vh", maxWidth: 1100, margin: "0 auto", padding: "28px 20px" }}>

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 28, left: "50%", transform: "translateX(-50%)",
          background: "#1c1c20", border: "0.5px solid #3a3a44", borderRadius: 24,
          padding: "10px 22px", color: "#c0c0c0", fontSize: 13, zIndex: 9999,
          boxShadow: "0 4px 24px rgba(0,0,0,0.5)", whiteSpace: "nowrap",
          fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}>{toast}</div>
      )}

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{
            fontFamily: "'Bricolage Grotesque', sans-serif",
            fontSize: 28, fontWeight: 800, color: "#f0f0f0",
            marginBottom: 6, letterSpacing: "-0.5px",
          }}>discussions</h1>
          <p style={{ fontSize: 13, color: "#55555e" }}>debates, hot takes, scene reactions — real guff only</p>
        </div>
        <button onClick={() => openForm("general")} style={{
          background: "#FF5C35", color: "#fff", padding: "10px 20px",
          borderRadius: 20, fontSize: 12, fontWeight: 700, border: "none",
          fontFamily: "'Bricolage Grotesque', sans-serif", cursor: "pointer",
        }}>+ start a guff</button>
      </div>

      {/* Post form */}
      {showForm && (
        <div ref={formRef} style={{ background: "#111113", border: "0.5px solid #252528", borderRadius: 14, padding: 20, marginBottom: 24 }}>
          {!formType ? (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#e0e0e0", fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                  what kind of guff?
                </span>
                <button onClick={() => setShowForm(false)} style={{ background: "none", border: "none", color: "#55555e", cursor: "pointer", fontSize: 20, lineHeight: 1, padding: 0 }}>×</button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))", gap: 8 }}>
                {POST_TYPES.map(pt => (
                  <div key={pt.key} onClick={() => setFormType(pt.key)} style={{
                    background: pt.bg, border: "0.5px solid #2a2a2c",
                    borderRadius: 10, padding: "12px 8px", textAlign: "center", cursor: "pointer",
                    transition: "border-color 0.15s",
                  }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = "#FF5C35"}
                    onMouseLeave={e => e.currentTarget.style.borderColor = "#2a2a2c"}
                  >
                    <div style={{ fontSize: 18, marginBottom: 6 }}>{pt.icon}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#c0c0c0", fontFamily: "'Bricolage Grotesque', sans-serif" }}>{pt.name}</div>
                    <div style={{ fontSize: 10, color: "#55555e", marginTop: 2 }}>{pt.desc}</div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <span style={{ fontSize: 16 }}>{activeFormType?.icon}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#e0e0e0", fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                  {activeFormType?.name}
                </span>
                <button onClick={() => setFormType(null)} style={{ background: "none", border: "none", color: "#55555e", cursor: "pointer", fontSize: 11, marginLeft: 4 }}>← change</button>
                <button onClick={() => setShowForm(false)} style={{ background: "none", border: "none", color: "#55555e", cursor: "pointer", fontSize: 20, lineHeight: 1, padding: 0, marginLeft: "auto" }}>×</button>
              </div>

              <textarea
                value={form.question}
                onChange={e => setForm(f => ({ ...f, question: e.target.value }))}
                placeholder="what's your take? be real..."
                rows={3}
                style={{ ...iStyle, resize: "vertical", marginBottom: 10 }}
              />

              {formType === "battle" && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                  <input value={form.side_a} onChange={e => setForm(f => ({ ...f, side_a: e.target.value }))} placeholder="Side A" style={iStyle} />
                  <input value={form.side_b} onChange={e => setForm(f => ({ ...f, side_b: e.target.value }))} placeholder="Side B" style={iStyle} />
                </div>
              )}

              {formType === "scene" && (
                <>
                  <input
                    value={form.scene_tag}
                    onChange={e => setForm(f => ({ ...f, scene_tag: e.target.value }))}
                    placeholder="scene tag (e.g. act 2 · no major spoilers)"
                    style={{ ...iStyle, marginBottom: 8 }}
                  />
                  <textarea
                    value={form.quote}
                    onChange={e => setForm(f => ({ ...f, quote: e.target.value }))}
                    placeholder="memorable quote..."
                    rows={2}
                    style={{ ...iStyle, resize: "vertical", marginBottom: 10 }}
                  />
                </>
              )}

              {formError && (
                <div style={{ fontSize: 12, color: "#FF5C35", marginBottom: 10 }}>{formError}</div>
              )}

              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={handleSubmit} disabled={submitting} style={{
                  background: "#FF5C35", color: "#fff", padding: "8px 20px",
                  borderRadius: 20, fontSize: 12, fontWeight: 700, border: "none",
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  cursor: submitting ? "default" : "pointer",
                  opacity: submitting ? 0.6 : 1,
                }}>{submitting ? "posting…" : "post guff"}</button>
                <button onClick={() => setShowForm(false)} style={{
                  background: "transparent", color: "#55555e",
                  border: "0.5px solid #252528", padding: "8px 20px",
                  borderRadius: 20, fontSize: 12, cursor: "pointer",
                }}>cancel</button>
              </div>
            </>
          )}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 250px", gap: 24 }}>

        {/* Main feed */}
        <div>
          {/* Type filter */}
          <div className="disc-type-filter" style={{ display: "flex", gap: 4, marginBottom: 14, flexWrap: "wrap" }}>
            {TYPES.map(t => (
              <button key={t.key} onClick={() => setActiveType(t.key)} style={{
                fontSize: 11, padding: "5px 14px", borderRadius: 20, whiteSpace: "nowrap",
                border: `0.5px solid ${activeType === t.key ? "#FF5C35" : "#252528"}`,
                background: activeType === t.key ? "#FF5C35" : "transparent",
                color: activeType === t.key ? "#fff" : "#55555e",
                cursor: "pointer", transition: "all 0.15s",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}>{t.label}</button>
            ))}
          </div>

          {/* Sort filter */}
          <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
            {SORT_OPTIONS.map(s => (
              <button key={s} onClick={() => setActiveSort(s)} style={{
                fontSize: 11, padding: "4px 12px", borderRadius: 20,
                border: `0.5px solid ${activeSort === s ? "#3a2a1f" : "#252528"}`,
                background: activeSort === s ? "#1f1208" : "transparent",
                color: activeSort === s ? "#FF5C35" : "#55555e",
                cursor: "pointer", transition: "all 0.15s",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}>{s}</button>
            ))}
          </div>

          {/* Cards */}
          <div style={{ background: "#111113", border: "0.5px solid #252528", borderRadius: 14, overflow: "hidden" }}>
            {loading ? (
              <div style={{ padding: 40, textAlign: "center", color: "#55555e", fontSize: 13, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                loading…
              </div>
            ) : discussions.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: "#55555e", fontSize: 13, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                no {activeType === "all" ? "" : activeType + " "}discussions yet — be the first!
              </div>
            ) : (
              discussions.map(d => (
                <DiscussionCard
                  key={d.id}
                  d={d}
                  showToast={showToast}
                  onDelete={handleDelete}
                  onUpdate={handleUpdate}
                />
              ))
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div>

          {/* Top battle (dynamic) */}
          {weeklyBattle && (() => {
            const a     = weeklyBattle.votes?.a || 0;
            const b     = weeklyBattle.votes?.b || 0;
            const total = a + b || 1;
            const pctA  = Math.round((a / total) * 100);
            const pctB  = 100 - pctA;
            return (
              <div style={{ background: "#111113", border: "0.5px solid #252528", borderRadius: 14, padding: 16, marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: "#FF5C35", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 6, fontWeight: 700 }}>
                  ⚔ top guff battle
                </div>
                <div style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 13, fontWeight: 700, color: "#e0e0e0", marginBottom: 12, lineHeight: 1.4 }}>
                  "{weeklyBattle.question}"
                </div>
                <div style={{ height: 6, background: "#1c1c1f", borderRadius: 3, overflow: "hidden", display: "flex", marginBottom: 8 }}>
                  <div style={{ width: `${pctA}%`, background: "#FF5C35" }} />
                  <div style={{ width: `${pctB}%`, background: "#8b7dd8" }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 11 }}>
                  <div>
                    <div style={{ color: "#55555e" }}>{weeklyBattle.side_a || "Side A"}</div>
                    <div style={{ color: "#FF5C35", fontWeight: 700, fontFamily: "'Bricolage Grotesque', sans-serif" }}>{pctA}%</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ color: "#55555e" }}>{weeklyBattle.side_b || "Side B"}</div>
                    <div style={{ color: "#8b7dd8", fontWeight: 700, fontFamily: "'Bricolage Grotesque', sans-serif" }}>{pctB}%</div>
                  </div>
                </div>
                <div style={{ fontSize: 10, color: "#55555e", textAlign: "center" }}>
                  {(a + b).toLocaleString()} voted
                </div>
              </div>
            );
          })()}

          {/* Trending debates (dynamic) */}
          {trendingDebates.length > 0 && (
            <div style={{ background: "#111113", border: "0.5px solid #252528", borderRadius: 14, padding: 16, marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: "#55555e", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 14 }}>
                trending debates
              </div>
              {trendingDebates.map((t, i) => (
                <div key={t.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0", borderBottom: i < trendingDebates.length - 1 ? "0.5px solid #1c1c1f" : "none" }}>
                  <div style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 14, fontWeight: 800, color: "#2a2a30", minWidth: 18 }}>
                    {i + 1}
                  </div>
                  <div style={{ flex: 1, fontSize: 12, color: "#666", lineHeight: 1.5 }}>{t.question}</div>
                  <div style={{ fontSize: 10, color: "#55555e", whiteSpace: "nowrap" }}>
                    {totalVotes(t) > 0 ? `${totalVotes(t)} votes` : `${t.reply_count || 0} replies`}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Start a discussion shortcuts */}
          <div style={{ background: "#111113", border: "0.5px solid #252528", borderRadius: 14, padding: 16 }}>
            <div style={{ fontSize: 10, color: "#55555e", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 12 }}>
              start a discussion
            </div>
            {POST_TYPES.map((pt, i) => (
              <div key={pt.key} onClick={() => openForm(pt.key)} style={{
                display: "flex", alignItems: "center", gap: 10, padding: "7px 0",
                borderBottom: i < POST_TYPES.length - 1 ? "0.5px solid #1c1c1f" : "none",
                cursor: "pointer",
              }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 6, background: pt.bg,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 13, flexShrink: 0,
                }}>{pt.icon}</div>
                <div>
                  <div style={{ fontSize: 12, color: "#c0c0c0", fontWeight: 700, fontFamily: "'Bricolage Grotesque', sans-serif" }}>{pt.name}</div>
                  <div style={{ fontSize: 10, color: "#55555e" }}>{pt.desc}</div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
}
